import { useQuery, useInfiniteQuery } from '@tanstack/react-query';

import { configs } from '@hero/configs';
import { PRODUCT_LIST_PAGINATION_LIMIT, QUERY_KEYS, STALE_TIME } from '@hero/constants';
import { productServices } from '@hero/services';
import { cleanObjectEmptyFields, createQueryKey } from '@hero/utils';

const getProductsQueryKeys = ({ queryKeyPrefix, params }) => {
  const {
    filters: {
      parentProductCode = '',
      productCodes = [],
      minPrice = '',
      maxPrice = '',
      searchTerm = '',
      categoryId = '',
      categoryIds = [],
      collectionIds = [],
      brandIds = [],
      merchantProductIds = [],
      skus = [],
      charityOnly = ''
    } = {},
    pagination: { limit = PRODUCT_LIST_PAGINATION_LIMIT, sort = '', page = 1 } = {}
  } = params;

  const queryParams = cleanObjectEmptyFields({
    filters: {
      parentProductCode,
      productCodes,
      minPrice,
      maxPrice,
      searchTerm,
      categoryId,
      categoryIds,
      collectionIds,
      merchantProductIds,
      skus,
      brandIds,
      charityOnly
    },
    pagination: { limit, sort, page }
  });

  return {
    queryParams,
    queryKey: createQueryKey(queryKeyPrefix, queryParams)
  };
};

const useSearchInfiniteProducts = (params = {}, options = {}) => {
  const { queryKey, queryParams } = getProductsQueryKeys({
    queryKeyPrefix: QUERY_KEYS.useGetInfiniteProductsKey,
    params
  });

  const isCursorBasedPagination = configs?.general?.cursorBasedPagination;

  return useInfiniteQuery(
    queryKey,
    async ({ pageParam, signal }) => {
      const restPagination = isCursorBasedPagination
        ? {
            type: 'cursor',
            cursor: pageParam?.nextCursor
          }
        : {
            page: pageParam?.nextPage || 1
          };

      const products = await productServices.searchProducts(
        {
          ...queryParams,
          pagination: {
            ...queryParams.pagination,
            ...restPagination
          }
        },
        signal
      );

      return products;
    },
    {
      getNextPageParam: lastPage => {
        return lastPage?.pagination?.hasNextPage && lastPage?.pagination;
      },
      refetchOnMount: false,
      ...options
    }
  );
};

const useSearchProducts = ({ params = {}, options = {} } = {}) => {
  const { queryKey, queryParams } = getProductsQueryKeys({
    queryKeyPrefix: QUERY_KEYS.useGetProductsKey + options?.customQueryKey || '',
    params
  });

  return useQuery(
    queryKey,
    async () => {
      return productServices.searchProducts(queryParams);
    },
    {
      staleTime: STALE_TIME.useGetProducts,
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
      retry: 1,
      retryDelay: 5000,
      refetchOnMount: false,
      ...options
    }
  );
};

const useSearchProductsByCodes = ({ params = {}, options = {} } = {}) => {
  const { queryKey, queryParams } = getProductsQueryKeys({
    queryKeyPrefix: QUERY_KEYS.useGetProductsByCodesKey,
    params
  });

  const productQuery = useQuery(
    queryKey,
    async () => {
      return productServices.searchProducts(queryParams);
    },
    {
      staleTime: STALE_TIME.useGetProducts,
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
      retry: 1,
      retryDelay: 5000,
      refetchOnMount: false,
      ...options
    }
  );

  return productQuery;
};

export const SEARCH_PRODUCTS_RESPONSE_TYPE = {
  bestSellers: 'bestSellers',
  default: 'default'
};
const useSearchProductsWithSKUs = ({ params = {}, options = {}, customOptions = {} } = {}) => {
  const { queryKey, queryParams } = getProductsQueryKeys({
    queryKeyPrefix: QUERY_KEYS.useGetProductsKey + options?.customQueryKey || '',
    params
  });

  return useQuery(
    queryKey,
    async () => {
      let responseProducts = {};
      if (customOptions?.responseType != SEARCH_PRODUCTS_RESPONSE_TYPE.bestSellers) {
        responseProducts = await productServices.searchProducts(queryParams);
      }

      if (!responseProducts?.docs?.length) {
        const { queryParams: bestSellersQueryParams } = getProductsQueryKeys({
          queryKeyPrefix: QUERY_KEYS.useGetProductsKey + options?.customQueryKey || '',
          params: {
            ...params,
            filters: {
              categoryIds: [configs?.productList?.defaultFilterId]
            }
          }
        });
        responseProducts = await productServices.searchProducts(bestSellersQueryParams);
        responseProducts.responseType = SEARCH_PRODUCTS_RESPONSE_TYPE.bestSellers;
      }

      const responseProductsWithSKUs = await Promise.all(
        responseProducts.docs.map(async product => {
          const productDetail = await productServices.getProductDetail(product.id);
          return {
            ...product,
            ...productDetail
          };
        })
      );

      const { minPrice, maxPrice } = params?.filters || {};
      if (minPrice && maxPrice) {
        responseProducts.docs = responseProductsWithSKUs.map(product => {
          return {
            ...product,
            skus: product?.skus?.filter(sku => sku.price >= minPrice && sku.price <= maxPrice) || []
          };
        });
      } else {
        responseProducts.docs = responseProductsWithSKUs.map(product => {
          return {
            ...product,
            skus: product?.skus?.filter(sku => sku.price === product.minPrice) || []
          };
        });
      }

      return responseProducts;
    },
    {
      staleTime: STALE_TIME.useGetProducts,
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
      retry: 1,
      retryDelay: 5000,
      refetchOnMount: false,
      ...options
    }
  );
};

export {
  useSearchInfiniteProducts,
  useSearchProducts,
  useSearchProductsByCodes,
  useSearchProductsWithSKUs
};
